/*
 * SearchableSelect: single-value dropdown with a search box (register form: business type,
 * "How did you find us").
 * This file and styles.module.css are kept byte-identical between `web` (TH) and `kh_web` (KH):
 * change one repo, copy the files to the other.
 *
 * Props:
 * - id: base id for the listbox / option ids (a11y), e.g. "signup-scope"
 * - value, onChange(value, option), onBlur(): controlled value (react-hook-form `field`)
 * - options: [{ value, label }]
 * - placeholder, searchPlaceholder, noResultsText: translated texts from the caller
 * - startIcon: icon element shown at the start of the trigger
 * - error: red border; disabled; className: root wrapper class
 * - ref: forwarded to the trigger button (react-hook-form focuses it on validation error)
 * - other props (data-test, aria-*) go to the trigger button (aria-label defaults to placeholder)
 * - each option <li> has data-value={option.value} (QA automation)
 *
 * The panel is an MUI Popover (portal to document.body, z-index above MUI Dialog), so it is never
 * clipped by the dialog / scroll container overflow. Popover is a Modal: while open it is the top
 * modal, so the focus trap of the surrounding Dialog pauses and the search box can keep focus
 * (a plain Popper portal inside a Dialog would get its focus stolen back by the Dialog).
 */
import { Popover } from '@material-ui/core';
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import styles from './styles.module.css';

const PANEL_Z_INDEX = 1400; // above MUI Modal / Dialog (theme.zIndex.modal = 1300)
const PANEL_GAP = 6; // px between trigger and panel
const VIEWPORT_MARGIN = 16; // Popover marginThreshold
const LIST_MAX_HEIGHT = 300;
const LIST_MIN_HEIGHT = 120;
const OPTION_HEIGHT = 36; // keep in sync with .option min-height
const PANEL_CHROME_HEIGHT = 72; // .panel padding (2 x 12) + .search height (40) + .list margin-top (8)

const isEmptyValue = (value) => value === undefined || value === null || value === '';

// Latin combining diacritics only (U+0300-U+036F). Do not use \p{M}: it would also strip Thai / Khmer
// vowel signs and tone marks.
const LATIN_DIACRITICS = /[\u0300-\u036f]/g;

// Search key: lower case, trimmed, Latin accents removed ("Nhà thuốc" -> "nha thuoc", "đ" -> "d").
// Thai / Khmer text is unchanged.
const normalizeText = (text) => {
  const str = String(text || '')
    .toLowerCase()
    .trim();
  const stripped =
    typeof str.normalize === 'function' ? str.normalize('NFD').replace(LATIN_DIACRITICS, '') : str;
  return stripped.replace(/đ/g, 'd');
};

// Visible area in layout-viewport coordinates. visualViewport excludes the iOS virtual keyboard,
// which changes neither window.innerHeight nor fires window resize.
const getVisibleArea = () => {
  const vv = window.visualViewport;
  if (vv && vv.height > 0) return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
  return { top: 0, bottom: window.innerHeight };
};

const isSameLayout = (a, b) =>
  a.width === b.width && a.placeAbove === b.placeAbove && a.listMaxHeight === b.listMaxHeight;

// No search autofocus on touch devices (would pop up the virtual keyboard). `isMobile` misses
// iPadOS (desktop UA), hence also `(pointer: coarse)`.
const shouldAutoFocusSearch = () => {
  if (isMobile) return false;
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return !window.matchMedia('(pointer: coarse)').matches;
  }
  return true;
};

const SearchableSelect = forwardRef((props, ref) => {
  const {
    id,
    name,
    value,
    options = [],
    onChange,
    onBlur,
    placeholder = '',
    searchPlaceholder = '',
    noResultsText = '',
    startIcon = null,
    error = false,
    disabled = false,
    className,
    ...rest
  } = props;

  const baseId = id || name || 'searchable-select';
  const listboxId = `${baseId}-listbox`;
  const getOptionId = (index) => `${baseId}-option-${index}`;

  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const popoverActionRef = useRef(null);
  const openRef = useRef(false);
  const restoreFocusRef = useRef(false);
  const keyboardNavRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [layout, setLayout] = useState({
    width: 0,
    placeAbove: false,
    listMaxHeight: LIST_MAX_HEIGHT,
  });

  openRef.current = open;

  const selectedOption = options.find((option) => option.value === value);
  const hasValue = !isEmptyValue(value);
  const displayText = (selectedOption && selectedOption.label) || value;

  const filteredOptions = useMemo(() => {
    const keyword = normalizeText(query);
    if (!keyword) return options;
    return options.filter((option) => normalizeText(option.label).includes(keyword));
  }, [options, query]);

  const activeOption = open ? filteredOptions[activeIndex] : undefined;
  // aria-controls only while the listbox is rendered
  const listboxControls = open && filteredOptions.length > 0 ? listboxId : undefined;

  const setTriggerRef = useCallback(
    (node) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // Panel width = trigger width; open below the trigger, flip above when there is not enough space
  const computeLayout = useCallback(() => {
    const node = triggerRef.current;
    if (!node || typeof window === 'undefined') return null;
    const rect = node.getBoundingClientRect();
    const visible = getVisibleArea();
    const spaceBelow = visible.bottom - rect.bottom - VIEWPORT_MARGIN - PANEL_GAP;
    const spaceAbove = rect.top - visible.top - VIEWPORT_MARGIN - PANEL_GAP;
    const listHeight = Math.min(LIST_MAX_HEIGHT, Math.max(options.length, 1) * OPTION_HEIGHT);
    const placeAbove = spaceBelow < listHeight + PANEL_CHROME_HEIGHT && spaceAbove > spaceBelow;
    const space = placeAbove ? spaceAbove : spaceBelow;
    return {
      width: rect.width,
      placeAbove,
      listMaxHeight: Math.max(
        LIST_MIN_HEIGHT,
        Math.min(LIST_MAX_HEIGHT, space - PANEL_CHROME_HEIGHT),
      ),
    };
  }, [options.length]);

  const scrollOptionIntoView = (index, center) => {
    const list = listRef.current;
    const optionNode =
      list && index >= 0 ? list.querySelector(`[data-option-index="${index}"]`) : null;
    if (!optionNode) return;
    if (center) {
      list.scrollTop = optionNode.offsetTop - (list.clientHeight - optionNode.offsetHeight) / 2;
      return;
    }
    if (optionNode.offsetTop < list.scrollTop) {
      list.scrollTop = optionNode.offsetTop;
    } else if (
      optionNode.offsetTop + optionNode.offsetHeight >
      list.scrollTop + list.clientHeight
    ) {
      list.scrollTop = optionNode.offsetTop + optionNode.offsetHeight - list.clientHeight;
    }
  };

  const openPanel = () => {
    if (disabled || openRef.current) return;
    const nextLayout = computeLayout();
    if (nextLayout) setLayout(nextLayout);
    setQuery('');
    setActiveIndex(options.findIndex((option) => option.value === value));
    setOpen(true);
  };

  const closePanel = (restoreFocus) => {
    if (!openRef.current) return;
    restoreFocusRef.current = !!restoreFocus;
    setOpen(false);
    if (onBlur) onBlur();
  };

  const handleSelect = (option) => {
    if (!option) return;
    if (onChange) onChange(option.value, option);
    closePanel(true);
  };

  const moveActive = (step) => {
    const count = filteredOptions.length;
    if (!count) return;
    keyboardNavRef.current = true;
    setActiveIndex((prev) => {
      if (prev < 0 || prev >= count) return step > 0 ? 0 : count - 1;
      return Math.min(count - 1, Math.max(0, prev + step));
    });
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openPanel();
    }
  };

  // Safari does not focus a <button> on click: focus it so MUI restores focus to the trigger
  // when the panel is closed by an outside click.
  const handleTriggerMouseDown = () => {
    if (!disabled && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };

  const handleTriggerBlur = () => {
    // focus moving into the open panel is not a blur of the field
    if (!openRef.current && onBlur) onBlur();
  };

  const handlePanelKeyDown = (event) => {
    if (event.nativeEvent && event.nativeEvent.isComposing) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        handleSelect(filteredOptions[activeIndex]);
        break;
      case 'Escape':
        // stop here so the surrounding Dialog does not close too
        event.preventDefault();
        event.stopPropagation();
        closePanel(true);
        break;
      case 'Tab':
        event.preventDefault();
        closePanel(true);
        break;
      default:
        break;
    }
  };

  const handleQueryChange = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    setActiveIndex(
      normalizeText(nextQuery) ? 0 : options.findIndex((option) => option.value === value),
    );
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const handleEntering = () => {
    scrollOptionIntoView(
      options.findIndex((option) => option.value === value),
      true,
    );
    if (shouldAutoFocusSearch() && searchRef.current) {
      searchRef.current.focus({ preventScroll: true });
    }
  };

  const handleExited = () => {
    setQuery('');
    setActiveIndex(-1);
    const trigger = triggerRef.current;
    if (restoreFocusRef.current && trigger && document.activeElement !== trigger) {
      trigger.focus({ preventScroll: true });
    }
    restoreFocusRef.current = false;
  };

  const handlePopoverClose = (event, reason) => {
    closePanel(reason === 'escapeKeyDown');
  };

  // keyboard navigation keeps the active option visible
  useEffect(() => {
    if (!open || !keyboardNavRef.current) return;
    keyboardNavRef.current = false;
    scrollOptionIntoView(activeIndex, false);
  }, [open, activeIndex]);

  // panel height changes while filtering: re-anchor (matters when the panel is flipped above)
  useEffect(() => {
    if (open && popoverActionRef.current) popoverActionRef.current.updatePosition();
  }, [open, filteredOptions.length, layout]);

  // Re-fit the panel when the visible area changes: window resize, and visualViewport resize/scroll
  // (iOS virtual keyboard opening while typing in the search box).
  useEffect(() => {
    if (!open) return undefined;
    const vv = window.visualViewport;
    let frame = null;
    const handleViewportChange = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const nextLayout = computeLayout();
        if (nextLayout) setLayout((prev) => (isSameLayout(prev, nextLayout) ? prev : nextLayout));
        if (popoverActionRef.current) popoverActionRef.current.updatePosition();
      });
    };
    window.addEventListener('resize', handleViewportChange);
    if (vv) {
      vv.addEventListener('resize', handleViewportChange);
      vv.addEventListener('scroll', handleViewportChange);
    }
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleViewportChange);
      if (vv) {
        vv.removeEventListener('resize', handleViewportChange);
        vv.removeEventListener('scroll', handleViewportChange);
      }
    };
  }, [open, computeLayout]);

  return (
    <div className={clsx(styles.root, className)}>
      <button
        aria-label={placeholder || undefined}
        {...rest}
        ref={setTriggerRef}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxControls}
        aria-invalid={error || undefined}
        disabled={disabled}
        className={clsx(styles.trigger, open && styles.trigger_open, error && styles.trigger_error)}
        onMouseDown={handleTriggerMouseDown}
        onClick={openPanel}
        onKeyDown={handleTriggerKeyDown}
        onBlur={handleTriggerBlur}
      >
        {startIcon && <span className={styles.start_icon}>{startIcon}</span>}
        <span className={clsx(styles.value, !hasValue && styles.placeholder)}>
          {hasValue ? displayText : placeholder}
        </span>
        <ExpandMoreIcon className={styles.chevron} />
      </button>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: layout.placeAbove ? 'top' : 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: layout.placeAbove ? 'bottom' : 'top', horizontal: 'left' }}
        marginThreshold={VIEWPORT_MARGIN}
        action={popoverActionRef}
        style={{ zIndex: PANEL_Z_INDEX }}
        TransitionProps={{ onEntering: handleEntering, onExited: handleExited }}
        PaperProps={{
          className: styles.paper,
          style: {
            width: layout.width || undefined,
            marginTop: layout.placeAbove ? -PANEL_GAP : PANEL_GAP,
          },
          onKeyDown: handlePanelKeyDown,
        }}
      >
        <div className={styles.panel}>
          <div className={styles.search}>
            <SearchIcon className={styles.search_icon} />
            <input
              ref={searchRef}
              type="text"
              className={styles.search_input}
              value={query}
              onChange={handleQueryChange}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listboxControls}
              aria-autocomplete="list"
              aria-activedescendant={activeOption ? getOptionId(activeIndex) : undefined}
              autoComplete="off"
            />
          </div>
          {filteredOptions.length > 0 ? (
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={placeholder}
              className={styles.list}
              style={{ maxHeight: layout.listMaxHeight }}
            >
              {filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <li
                    key={option.value}
                    id={getOptionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    data-option-index={index}
                    data-value={option.value}
                    className={clsx(
                      styles.option,
                      index === activeIndex && styles.option_active,
                      isSelected && styles.option_selected,
                    )}
                    onMouseMove={() => {
                      if (activeIndex !== index) setActiveIndex(index);
                    }}
                    onClick={() => handleSelect(option)}
                  >
                    <span className={styles.option_label}>{option.label}</span>
                    {isSelected && <CheckIcon className={styles.option_check} />}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className={styles.empty} role="status">
              {noResultsText}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
});

export default SearchableSelect;
